import styled from '@emotion/styled';
import {
  breakpoints,
  fontSize,
  Modal,
  palette,
  Progress,
  spacing,
  Button,
} from '@playdapp/ui';
import { useQuery } from 'react-query';

import { getFeeValue } from 'api/order';
import useFeeValues from 'hooks/useFeeValues';
import type { NetworkName } from 'types/network';

import Icon, { Icons } from '@/components/Icon';
import Image from '@/components/Image';
import Video from '@/components/Video';
import WarningText from '@/components/WarningText';

type Props = {
  isOpen: boolean;
  isClicked?: boolean;
  isLoading: boolean;
  name: string;
  currency: string;
  price: number;
  imgUrl?: string;
  network: NetworkName;
  contractAddress: string;
  toUsdPrice?: string | null;
  isPostable?: boolean;
  handleOpen: (isOpen: boolean) => void;
  handleAccept: () => void;
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxl};
`;

const ModalTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${breakpoints.down('md')} {
    margin-bottom: ${spacing.s};
  }
`;

const ModalTitle = styled.h3`
  font-size: ${fontSize.h3};
  font-weight: 900;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.h5};
  }
`;

const ModalContentsBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemColumn = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.m};

  ${breakpoints.down('md')} {
    display: none;
  }
`;

const Label = styled.span<{ color?: string }>`
  font-weight: 700;
  color: ${({ color }) => color ?? palette.black};
`;

const LabelValue = styled.span<{ color?: string }>`
  font-size: ${fontSize.p3};
  color: ${({ color }) => color ?? palette.black};
`;

const ItemInfoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.m};
  padding: ${spacing.m} 0;
  border-top: 1px solid ${palette.gray400};
  border-bottom: 1px solid ${palette.gray400};
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacing.s};
`;

const ImageBox = styled.figure`
  max-width: 80px;
  max-height: 80px;

  video {
    border-radius: 16px;
  }
`;

const ItemName = styled.p`
  font-size: ${fontSize.p3};
`;

const PriceBox = styled.div`
  display: flex;

  ${breakpoints.down('md')} {
    flex-direction: column;
  }
`;

const IconBox = styled.div`
  display: flex;
  align-items: center;
`;

const Price = styled.span`
  font-size: ${fontSize.p3};
  margin: 0 ${spacing.s};
`;

const CurrencyBox = styled.div`
  ${breakpoints.down('md')} {
    display: block;
  }
`;

const UsdPrice = styled.span`
  font-size: ${fontSize.p4};
  color: ${palette.gray900};
`;

const FeeBlock = styled.div`
  margin-top: ${spacing.xxl};
  margin-bottom: ${spacing.m};

  ${breakpoints.down('md')} {
    margin-top: ${spacing.xl};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeeBox = styled.div<{ isError: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: ${spacing.m};
  padding: ${spacing.l};
  border: 1px solid
    ${({ isError }) => (isError ? palette.red : palette.gray400)};
  border-radius: 0.75rem;
`;

const FeeColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalBlock = styled.div`
  padding-top: 10px;
  border-top: 1px solid ${palette.gray400};
`;

const DescriptionText = styled.p`
  word-break: break-all;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.s};

  button {
    flex: 1;
    font-weight: 600;
  }

  ${breakpoints.down('md')} {
    flex-direction: column;

    button {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const OfferAcceptModal = ({
  isOpen,
  isClicked,
  isLoading,
  name,
  currency,
  toUsdPrice,
  price,
  contractAddress,
  imgUrl,
  network,
  isPostable,
  handleOpen,
  handleAccept,
}: Props) => {
  const {
    data: fee,
    isLoading: isFeeLoading,
    isError: isFeeError,
  } = useQuery(
    'Fee',
    async () => {
      const { data } = await getFeeValue({
        network,
        contractAddress,
      });
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const { feeValues } = useFeeValues(fee, price.toString());

  return (
    <Modal
      width={{ xl: '500px', xxl: '500px' }}
      isOpen={isOpen}
      handleOpen={handleOpen}
      shouldCloseOnOverlayClick
    >
      <ModalContent>
        <ModalTitleBlock>
          <ModalTitle>Accept this offer</ModalTitle>
        </ModalTitleBlock>
        <ModalContentsBlock>
          <ItemColumn>
            <Label>Item</Label>
            <LabelValue>Subtotal</LabelValue>
          </ItemColumn>
          <ItemInfoBlock>
            <ImageBox>
              {/.(mp4|mov)$/.test(imgUrl as string) ? (
                <Video
                  isPlaydapp={false}
                  src={imgUrl as string}
                  muted
                  autoPlay
                  playsInline
                  loop
                />
              ) : (
                <Image src={imgUrl as string} alt={name} />
              )}
            </ImageBox>
            <InfoBox>
              <ItemName>{name}</ItemName>
              <PriceBox>
                <IconBox>
                  <Icon
                    name={`currency-${currency.toLowerCase()}` as Icons}
                    size="24px"
                  />
                  <Price>{price}</Price>
                </IconBox>
                <CurrencyBox>
                  {toUsdPrice && <UsdPrice>(≈ ${toUsdPrice})</UsdPrice>}
                </CurrencyBox>
              </PriceBox>
            </InfoBox>
          </ItemInfoBlock>
          <FeeBlock>
            <Label>Fee</Label>
            <FeeBox isError={isFeeError}>
              {isFeeLoading ? (
                <LoadingContainer>
                  <Progress type="spinner" color={palette.primary600} />
                </LoadingContainer>
              ) : (
                <>
                  <FeeRow>
                    <Label>PlayDapp</Label>
                    <LabelValue>
                      {feeValues.playdapp.price} {currency} (
                      {(feeValues.playdapp.percent * 100).toFixed(1)}%)
                    </LabelValue>
                  </FeeRow>
                  <FeeRow>
                    <FeeColumn>
                      <Label>Developer </Label>
                      (or Creator)
                    </FeeColumn>
                    <LabelValue>
                      {feeValues.developer.price} {currency} (
                      {(feeValues.developer.percent * 100).toFixed(1)}%)
                    </LabelValue>
                  </FeeRow>
                  <TotalBlock>
                    <FeeRow>
                      <Label color={palette.primary600}>Total</Label>
                      <LabelValue color={palette.primary600}>
                        <b>
                          {feeValues.total.price} {currency} (
                          {(feeValues.total.percent * 100).toFixed(1)}%)
                        </b>
                      </LabelValue>
                    </FeeRow>
                  </TotalBlock>
                </>
              )}
            </FeeBox>
          </FeeBlock>

          {isFeeError ? (
            <DescriptionText>
              An error occurred while getting fee data.
            </DescriptionText>
          ) : (
            <DescriptionText>
              You can get{' '}
              <Label color={palette.primary600}>
                {(isFeeLoading
                  ? 0
                  : Number(price) - feeValues.total.price
                ).toFixed(4)}{' '}
                {currency} {toUsdPrice && <span>(≈ ${toUsdPrice})</span>}
              </Label>{' '}
              when the sale completes.
            </DescriptionText>
          )}
        </ModalContentsBlock>
        <ButtonGroup>
          {isLoading ? (
            <Progress type="spinner" color={palette.primary600} />
          ) : (
            <Button
              onClick={handleAccept}
              isActive={isClicked}
              disabled={isClicked || !isPostable}
            >
              Accept
            </Button>
          )}
        </ButtonGroup>
        {!isPostable && (
          <WarningText marginTop="-20px">
            This asset does not have metadata and cannot be accepted for offer.
          </WarningText>
        )}
      </ModalContent>
    </Modal>
  );
};

export default OfferAcceptModal;
